import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { checkLoggedIn } from '../../functions/CookieHelper';
import { orderedPositions, positionsByShot } from '../../surveyPositions';
import { hasDepthRun } from '../../depthTrace';
import { LineChart } from '../controls/LineChart';
import { Line3DChart } from '../controls/Line3DChart';
import { TimeBasedChart } from '../controls/TimeBasedChart';
import { TwoSeriesTimeBasedChart } from '../controls/charts/TwoSeriesTimeBasedChart';
import { OrientationRecordChart } from '../controls/charts/OrientationRecordChart';
import { GyroRecordChart } from '../controls/charts/GyroRecordChart';
import { DepthRunPanel } from '../controls/DepthRunPanel';
import { FaChevronRight, FaChevronDown, FaFolder, FaFolderOpen, FaFile } from 'react-icons/fa';

export const Analysis = () => {
    const [loggedIn] = useState(checkLoggedIn());
    const [loading, setLoading] = useState(true);
    const [customerMap, setCustomerMap] = useState({});
    const [expandedNodes, setExpandedNodes] = useState({});
    const [selectedSurveyRef, setSelectedSurveyRef] = useState(null);
    const [survey, setSurvey] = useState(null);
    const [surveyLoading, setSurveyLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('table');

    useEffect(() => {
        if (!loggedIn) return;
        const loadCustomer = async () => {
            const response = await fetch('api/projects');
            const data = await response.json();
            setCustomerMap(data);
            setLoading(false);
        };
        loadCustomer();
    }, [loggedIn]);

    const toggleNode = (key) => {
        setExpandedNodes(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const loadSurvey = async (surveyRef) => {
        setSelectedSurveyRef(surveyRef);
        setSurveyLoading(true);
        setSurvey(null);
        const response = await fetch('api/surveys/' + surveyRef.fileId);
        const data = await response.json();
        setSurvey(data);
        setSurveyLoading(false);
        setActiveTab('table');
    };

    // Tree view rendering
    const renderTree = () => {
        if (!customerMap.projects) return null;

        const hasUnallocated = customerMap.unallocatedSurveys && customerMap.unallocatedSurveys.length > 0;

        return (
            <div style={{ fontSize: '0.875rem' }}>
                {hasUnallocated && (
                    <div>
                        <div
                            className="d-flex align-items-center py-1 px-2"
                            style={{ cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => toggleNode('unallocated')}
                        >
                            {expandedNodes['unallocated'] ? <FaChevronDown className="me-1" /> : <FaChevronRight className="me-1" />}
                            {expandedNodes['unallocated'] ? <FaFolderOpen className="me-1 text-warning" /> : <FaFolder className="me-1 text-warning" />}
                            Unallocated ({customerMap.unallocatedSurveys.length})
                        </div>
                        {expandedNodes['unallocated'] && (
                            <div style={{ paddingLeft: '1.5rem' }}>
                                {customerMap.unallocatedSurveys.map((s, i) => (
                                    <div
                                        key={i}
                                        className={`d-flex align-items-center py-1 px-2 ${selectedSurveyRef?.fileId === s.fileId ? 'bg-light fw-bold' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => loadSurvey(s)}
                                    >
                                        <FaFile className="me-1 text-muted" style={{ fontSize: '0.75rem' }} />
                                        <span className="text-truncate" title={s.fileName}>{s.fileName}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {customerMap.projects.map((project, pi) => {
                    const projectKey = `project-${pi}`;
                    const drillholes = project.drillholes || [];
                    const surveyCount = drillholes.reduce((sum, dh) => sum + (dh.surveys?.length || 0), 0);

                    return (
                        <div key={pi}>
                            <div
                                className="d-flex align-items-center py-1 px-2"
                                style={{ cursor: 'pointer', fontWeight: 600 }}
                                onClick={() => toggleNode(projectKey)}
                            >
                                {expandedNodes[projectKey] ? <FaChevronDown className="me-1" /> : <FaChevronRight className="me-1" />}
                                {expandedNodes[projectKey] ? <FaFolderOpen className="me-1 text-primary" /> : <FaFolder className="me-1 text-primary" />}
                                <span className="text-truncate" title={project.name}>{project.name}</span>
                                <span className="badge bg-secondary ms-auto">{surveyCount}</span>
                            </div>
                            {expandedNodes[projectKey] && drillholes.map((dh, di) => {
                                const dhKey = `dh-${pi}-${di}`;
                                const surveys = dh.surveys || [];
                                return (
                                    <div key={di} style={{ paddingLeft: '1rem' }}>
                                        <div
                                            className="d-flex align-items-center py-1 px-2"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => toggleNode(dhKey)}
                                        >
                                            {expandedNodes[dhKey] ? <FaChevronDown className="me-1" /> : <FaChevronRight className="me-1" />}
                                            {expandedNodes[dhKey] ? <FaFolderOpen className="me-1 text-info" /> : <FaFolder className="me-1 text-info" />}
                                            <span className="text-truncate" title={dh.name}>{dh.name}</span>
                                            <span className="badge bg-secondary ms-auto">{surveys.length}</span>
                                        </div>
                                        {expandedNodes[dhKey] && surveys.map((s, si) => (
                                            <div
                                                key={si}
                                                className={`d-flex align-items-center py-1 px-2 ${selectedSurveyRef?.fileId === s.fileId ? 'bg-light fw-bold' : ''}`}
                                                style={{ cursor: 'pointer', paddingLeft: '1.5rem' }}
                                                onClick={() => loadSurvey(s)}
                                            >
                                                <FaFile className="me-1 text-muted" style={{ fontSize: '0.75rem' }} />
                                                <span className="text-truncate" title={s.fileName}>{s.fileName}</span>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        );
    };

    return loggedIn ? (
        <div className="container-fluid" style={{ height: 'calc(100vh - 60px)', overflow: 'hidden' }}>
            {loading ? <p className="mt-3"><em>Loading...</em></p> : (
                <div className="row" style={{ height: '100%' }}>
                    {/* Left sidebar - tree view */}
                    <div className="col-md-3 col-lg-2 border-end" style={{ height: '100%', overflowY: 'auto', paddingTop: '0.5rem' }}>
                        {customerMap.logoUrl && (
                            <div className="d-flex justify-content-center mb-2">
                                <div className="logoFill" style={{
                                    minWidth: 120, height: 60,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundSize: 'contain',
                                    backgroundPosition: 'center',
                                    backgroundImage: `url(${customerMap.logoUrl})`
                                }} />
                            </div>
                        )}
                        {renderTree()}
                    </div>

                    {/* Right panel - survey detail */}
                    <div className="col-md-9 col-lg-10" style={{ height: '100%', overflowY: 'auto', paddingTop: '0.5rem' }}>
                        {!selectedSurveyRef && !surveyLoading && (
                            <div className="text-muted mt-5 text-center">
                                <h5>Select a survey from the tree to begin analysis</h5>
                            </div>
                        )}
                        {surveyLoading && <p><em>Loading survey...</em></p>}
                        {survey && !surveyLoading && (
                            <SurveyDetail survey={survey} activeTab={activeTab} setActiveTab={setActiveTab} />
                        )}
                    </div>
                </div>
            )}
        </div>
    ) : (
        <div>
            <h1>Not Logged in</h1>
            <p>Please <Link to="/login?returnPage=/analysis">login</Link></p>
        </div>
    );
};


const SurveyDetail = ({ survey, activeTab, setActiveTab }) => {
    const isContinuous = (survey.DeviceName || '').startsWith('FastTrak');

    return (
        <div>
            <div className="d-flex align-items-center mb-2">
                <h4 className="mb-0 me-3">{survey.Name}</h4>
                <div className="btn-group btn-group-sm ms-auto">
                    <button className={`btn ${activeTab === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setActiveTab('table')}>Data</button>
                    <button className={`btn ${activeTab === 'charts' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setActiveTab('charts')}>Charts</button>
                    <button className={`btn ${activeTab === 'diagnostics' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setActiveTab('diagnostics')}>Diagnostics</button>
                </div>
            </div>

            <SurveyMetadata survey={survey} />
            <hr />

            {activeTab === 'table' && <SurveyDataTab survey={survey} isContinuous={isContinuous} />}
            {activeTab === 'charts' && <SurveyChartsTab survey={survey} isContinuous={isContinuous} />}
            {activeTab === 'diagnostics' && <SurveyDiagnosticsTab survey={survey} isContinuous={isContinuous} />}
        </div>
    );
};


const SurveyMetadata = ({ survey }) => {
    return (
        <div className="row small">
            <div className="col-md-6">
                <table className="table table-sm table-borderless mb-0">
                    <tbody>
                        <tr><td className="fw-bold" style={{ width: '140px' }}>Device</td><td>{survey.DeviceName || '-'}</td></tr>
                        <tr><td className="fw-bold">Device ID</td><td>{survey.DeviceId ?? '-'}</td></tr>
                        <tr><td className="fw-bold">App Version</td><td>{survey.AppVersion || '-'}</td></tr>
                        <tr><td className="fw-bold">Device Type</td><td>{survey.DeviceType ?? '-'}</td></tr>
                    </tbody>
                </table>
            </div>
            <div className="col-md-6">
                <table className="table table-sm table-borderless mb-0">
                    <tbody>
                        <tr><td className="fw-bold" style={{ width: '140px' }}>Created</td><td>{survey.Created ? new Date(survey.Created).toLocaleString() : '-'}</td></tr>
                        <tr><td className="fw-bold">Last Modified</td><td>{survey.LastModified ? new Date(survey.LastModified).toLocaleString() : '-'}</td></tr>
                        <tr><td className="fw-bold">Location</td><td>{survey.Latitude != null ? `${survey.Latitude.toFixed(6)}, ${survey.Longitude.toFixed(6)}` : '-'}</td></tr>
                        <tr><td className="fw-bold">Shots</td><td>{survey.SavedShots?.length || 0} saved{survey.FailedShots?.length > 0 ? `, ${survey.FailedShots.length} failed` : ''}</td></tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};


const SurveyDataTab = ({ survey, isContinuous }) => {
    const approvedItems = survey.SavedShots?.filter(item => item.Approved !== false) || [];
    const rejectedItems = survey.SavedShots?.filter(item => item.Approved === false) || [];

    const sorted = isContinuous
        ? [...approvedItems].sort((a, b) => new Date(a.Timestamp) - new Date(b.Timestamp))
        : [...approvedItems].sort((a, b) => a.Depth - b.Depth);

    // Compute min-curv positions
    const positionByShot = useMemo(() => positionsByShot(survey), [survey]);

    // Depth statistics
    const depths = approvedItems.map(s => s.Depth);
    const maxDepth = depths.length > 0 ? Math.max(...depths) : 0;
    const minDepth = depths.length > 0 ? Math.min(...depths) : 0;

    return (
        <div>
            <div className="mb-3 small">
                <strong>Depth range:</strong> {minDepth.toFixed(1)}m to {maxDepth.toFixed(1)}m |
                <strong className="ms-2">Total shots:</strong> {approvedItems.length}
                {rejectedItems.length > 0 && <span className="text-danger"> ({rejectedItems.length} rejected)</span>}
            </div>

            <div className="table-responsive" style={{ maxHeight: '60vh', overflow: 'auto' }}>
                <table className="table table-striped table-sm table-hover" style={{ fontSize: '0.8rem' }}>
                    <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                            <th>#</th>
                            <th>Depth</th>
                            <th>Inclination</th>
                            <th>Azimuth</th>
                            <th>Highside</th>
                            <th>Steering</th>
                            <th>GTotal</th>
                            <th>WTotal</th>
                            <th>+N/-S</th>
                            <th>+E/-W</th>
                            <th>TVD</th>
                            <th>Timestamp</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((row, index) => {
                            const p = positionByShot.get(row);
                            return (
                                <tr key={row.Id ?? index}>
                                    <td>{index + 1}</td>
                                    <td>{row.Depth.toFixed(2)}</td>
                                    <td>{row.OrientationSet.Inclination.toFixed(2)}</td>
                                    <td>{row.OrientationSet.Azimuth.toFixed(2)}</td>
                                    <td>{row.OrientationSet.HighSide.toFixed(2)}</td>
                                    <td>{row.OrientationSet.SteeringAngle?.toFixed(2) ?? ''}</td>
                                    <td>{row.OrientationSet.GTotal?.toFixed(3) ?? ''}</td>
                                    <td>{row.OrientationSet.WTotal?.toFixed(2) ?? ''}</td>
                                    <td>{p ? p.northing.toFixed(2) : ''}</td>
                                    <td>{p ? p.easting.toFixed(2) : ''}</td>
                                    <td>{p ? p.tvd.toFixed(2) : ''}</td>
                                    <td>{row.Timestamp ? new Date(row.Timestamp).toLocaleTimeString() : ''}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {rejectedItems.length > 0 && (
                <div className="mt-3">
                    <h6>Rejected Shots ({rejectedItems.length})</h6>
                    <div className="table-responsive">
                        <table className="table table-striped table-sm" style={{ fontSize: '0.8rem' }}>
                            <thead>
                                <tr>
                                    <th>Depth</th>
                                    <th>Inclination</th>
                                    <th>Azimuth</th>
                                    <th>GTotal</th>
                                    <th>WTotal</th>
                                    <th>Timestamp</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rejectedItems.map((row, index) => (
                                    <tr key={index}>
                                        <td>{row.Depth.toFixed(2)}</td>
                                        <td>{row.OrientationSet.Inclination.toFixed(2)}</td>
                                        <td>{row.OrientationSet.Azimuth.toFixed(2)}</td>
                                        <td>{row.OrientationSet.GTotal?.toFixed(3) ?? ''}</td>
                                        <td>{row.OrientationSet.WTotal?.toFixed(2) ?? ''}</td>
                                        <td>{row.Timestamp ? new Date(row.Timestamp).toLocaleTimeString() : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};


const SurveyChartsTab = ({ survey, isContinuous }) => {
    const approvedItems = survey.SavedShots?.filter(item => item.Approved !== false) || [];

    const positions = useMemo(() => orderedPositions(survey), [survey]); // eslint-disable-line react-hooks/exhaustive-deps

    const planSeries = [{
        name: survey.Name,
        points: positions.map(p => [p.easting, p.northing])
    }];

    const sectionSeries = [{
        name: survey.Name,
        points: positions.map(p => [p.stepout, -p.tvd])
    }];

    const threeDSeries = [{
        name: survey.Name,
        points: positions.map(p => [p.easting, p.northing, -p.tvd])
    }];

    // Repeatability for continuous surveys
    const repeatability = useMemo(() => {
        if (!isContinuous || positions.length < 4) return null;
        const DEPTH_MATCH_TOLERANCE_M = 1.0;
        let peakIdx = 0;
        for (let i = 1; i < positions.length; i++) {
            if (positions[i].depth > positions[peakIdx].depth) peakIdx = i;
        }
        if (peakIdx === 0 || peakIdx === positions.length - 1) return null;

        const collarOffset = positions.length > approvedItems.length ? 1 : 0;
        const inrunOrdered = [];
        for (let i = collarOffset; i <= peakIdx; i++) inrunOrdered.push(i);
        inrunOrdered.sort((a, b) => positions[a].depth - positions[b].depth);

        let pair = null;
        for (const inrunIdx of inrunOrdered) {
            const inDepth = positions[inrunIdx].depth;
            let bestOutIdx = -1, bestDelta = Infinity;
            for (let j = peakIdx; j < positions.length; j++) {
                const delta = Math.abs(positions[j].depth - inDepth);
                if (delta < bestDelta) { bestDelta = delta; bestOutIdx = j; }
            }
            if (bestOutIdx >= 0 && bestDelta <= DEPTH_MATCH_TOLERANCE_M) {
                pair = { inrunIdx, outrunIdx: bestOutIdx, inrunDepth: inDepth, outrunDepth: positions[bestOutIdx].depth, depthDelta: bestDelta };
                break;
            }
        }
        if (!pair) return null;

        const a = positions[pair.inrunIdx];
        const b = positions[pair.outrunIdx];
        const dx = a.northing - b.northing;
        const dy = a.easting - b.easting;
        const dz = a.tvd - b.tvd;
        const radial = Math.sqrt(dx * dx + dy * dy + dz * dz);

        let pathLength = 0;
        const lo = Math.min(pair.inrunIdx, pair.outrunIdx);
        const hi = Math.max(pair.inrunIdx, pair.outrunIdx);
        for (let i = lo; i < hi; i++) {
            pathLength += Math.abs(positions[i + 1].depth - positions[i].depth);
        }
        const mismatchPct = pathLength > 0 ? (radial / pathLength) * 100 : 0;

        return { ...pair, radial, pathLength, mismatchPct };
    }, [positions]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            {repeatability && (
                <div className="card mb-3" style={{ backgroundColor: '#2c5f2d', color: '#fff' }}>
                    <div className="card-body py-2">
                        <h6 className="card-title mb-1">Survey Repeatability</h6>
                        <p className="card-text mb-0 small">
                            <strong>{repeatability.radial.toFixed(3)} m</strong> radial displacement at depth {repeatability.inrunDepth.toFixed(1)} m
                            ({repeatability.mismatchPct.toFixed(2)}% over {repeatability.pathLength.toFixed(1)} m path)
                        </p>
                    </div>
                </div>
            )}

            <div className="row">
                <div className="col-lg-6">
                    <LineChart title='Plan' xAxisName='East' yAxisName='North' seriesCollection={planSeries} />
                </div>
                <div className="col-lg-6">
                    <LineChart title='Section' xAxisName='Stepout' yAxisName='TVD' seriesCollection={sectionSeries} />
                </div>
                <div className="col-lg-12">
                    <Line3DChart title='3D' xAxisName='East' yAxisName='North' zAxisName='TVD' seriesCollection={threeDSeries} />
                </div>
            </div>
        </div>
    );
};


const SurveyDiagnosticsTab = ({ survey, isContinuous }) => {
    // Depth vs Time
    const depthTimeData = (survey.DepthTimeRecords || [])
        .sort((a, b) => new Date(a.TimeStamp) - new Date(b.TimeStamp))
        .map(r => [r.TimeStamp, r.Depth]);

    // Inclination vs Depth
    const approvedItems = (survey.SavedShots || []).filter(s => s.Approved !== false);
    const sorted = isContinuous
        ? [...approvedItems].sort((a, b) => new Date(a.Timestamp) - new Date(b.Timestamp))
        : [...approvedItems].sort((a, b) => a.Depth - b.Depth);

    const incVsDepthData = sorted.map(s => [s.Timestamp, s.OrientationSet.Inclination]);
    const azVsDepthData = sorted.map(s => [s.Timestamp, s.OrientationSet.Azimuth]);

    return (
        <div>
            {survey.OrientationRecords && survey.OrientationRecords.length > 0 && (
                <div className="mb-3">
                    <OrientationRecordChart orientationRecords={survey.OrientationRecords} depthRecords={survey.DepthTimeRecords} height={400} />
                </div>
            )}

            {/* One predicate, used here with the opposite sign to the panel's own
                gate, so there is a single answer to "does this survey have a
                counter run" rather than two that can drift. */}
            {!hasDepthRun(survey) && depthTimeData.length > 0 && (
                <div className="mb-3">
                    <TimeBasedChart title='Depth vs Time' yAxisName='Depth (m)' height={300} seriesName='Depth' seriesData={depthTimeData} />
                </div>
            )}

            {incVsDepthData.length > 0 && (
                <div className="mb-3">
                    <TwoSeriesTimeBasedChart title='Inclination & Azimuth vs Time' yAxis1Name='Inclination' yAxis2Name='Azimuth' height={300} plotOnSameAxis={false}
                        series1Name='Inclination' series1Data={incVsDepthData}
                        series2Name='Azimuth' series2Data={azVsDepthData} />
                </div>
            )}

            {survey.BatteryRecords && survey.BatteryRecords.length > 0 && (
                <div className="mb-3">
                    <TimeBasedChart title='Battery Voltage' yAxisName='Voltage (V)' height={300} seriesName='Battery' seriesData={survey.BatteryRecords.map(r => [r.Recieved, r.BatteryVolts])} />
                </div>
            )}

            {survey.PressureRecords && survey.PressureRecords.length > 0 && (
                <div className="mb-3">
                    <TwoSeriesTimeBasedChart title='Pressure Records' yAxis1Name='Pressure' yAxis2Name='' height={300} plotOnSameAxis={true}
                        series1Name='Annulus' series1Data={survey.PressureRecords.map(r => [r.TimeStamp, r.Pressure1])}
                        series2Name='Pipe' series2Data={survey.PressureRecords.map(r => [r.TimeStamp, r.Pressure2])} />
                </div>
            )}

            {survey.WirelinePsuRecords && survey.WirelinePsuRecords.length > 0 && (
                <div className="mb-3">
                    <TimeBasedChart title='Wireline PSU Voltage' yAxisName='Voltage (V)' height={300} seriesName='High Voltage' seriesData={survey.WirelinePsuRecords.map(r => [r.Recieved, r.HighVoltage])} />
                </div>
            )}

            {/* The raw gyro stream, drawn rather than described. This used to be a
                count and a note pointing at the drillhole view, which meant the
                one page devoted to looking at a survey was the one page that would
                not show you its sensor data. Same chart the other two views use. */}
            {survey.GyroRecords && survey.GyroRecords.length > 0 && (
                <div className="mb-3">
                    <GyroRecordChart gyroRecords={survey.GyroRecords}
                        depthRecords={survey.DepthTimeRecords} height={400} />
                </div>
            )}

            {/* Last, after the survey's own diagnostics: evidence about how the run
                was made rather than what it measured. It carries the depth-vs-time
                curve with the shots laid over it, which is why the plain Depth vs
                Time chart above stands down for those surveys rather than repeating
                half of it. Renders nothing otherwise, so it is dropped in bare. */}
            <DepthRunPanel survey={survey} />
        </div>
    );
};
